import {useEffect, useRef, useState} from 'react';
import mapboxgl, {type FeatureSelector} from 'mapbox-gl';
import getInitialMapCamera, {getGlobeZoom} from './getInitialMapCamera';
import useBaseStyle from 'src/hooks/useBaseStyle';
import useDarkMode from 'src/hooks/useDarkMode';
import {getVariantStyle} from './mapStyleUtils';
import getCenter from 'src/utils/map/getCenter';

export const clickableLayers = [
  'settlement-major-label',
  'settlement-minor-label',
  'singlePointPin',
  'clusteredPoints',
  'poi-label'
];

const getVariantMaxZoom = (variant: 'countries' | 'places') => (variant === 'places' ? 20 : 5);

type Options = {
  variant: 'countries' | 'places';
  highlightedCountryCodes?: string[];
  places: Partial<Place>[];
  themeOverride?: 'light' | 'dark';
  zoomOverride?: number;
  padding?: {top: number; right: number; bottom: number; left: number};
};

const useMapStyle = ({
  variant,
  highlightedCountryCodes,
  places,
  themeOverride,
  zoomOverride,
  padding
}: Options): {mapContainer: React.RefObject<HTMLDivElement | null>; map: mapboxgl.Map | null} => {
  const mapCreated = useRef(false);
  const [map, setMap] = useState<mapboxgl.Map | null>(null);
  const mapContainer = useRef<HTMLDivElement | null>(null);
  const hoveredFeatureRef = useRef<FeatureSelector | undefined>(undefined);

  const baseStyle = useBaseStyle();
  const {isDark: isDarkMode, isLoadingTheme} = useDarkMode();

  const isDark = themeOverride ? themeOverride === 'dark' : isDarkMode;

  const previousDarkMode = useRef(isDarkMode);
  const previousVariant = useRef<'countries' | 'places'>(variant);
  const previousHighlightedCountryCodes = useRef<string[] | undefined>(highlightedCountryCodes);
  const previousPlaces = useRef<Partial<Place>[]>(places);

  useEffect(() => {
    if (map || mapCreated.current || isLoadingTheme || !baseStyle || !mapContainer.current) return;

    mapCreated.current = true;

    const containerSize = mapContainer.current.getBoundingClientRect();
    const size = Math.min(containerSize.width, containerSize.height);

    const style = getVariantStyle({baseStyle, variant, isDark, highlightedCountryCodes, places});

    const localMap = new mapboxgl.Map({
      container: mapContainer.current,
      style,
      ...getInitialMapCamera(places, size, variant, zoomOverride),
      attributionControl: false
    });

    if (padding) localMap.setPadding(padding);

    localMap.on('load', () => {
      console.info('Map loaded!');

      localMap.on('mouseenter', clickableLayers, (event) => {
        localMap.getCanvas().style.cursor = 'pointer';

        const feature = event.features?.[0];

        if (feature?.source) {
          hoveredFeatureRef.current = {
            id: Number(feature.id),
            source: feature.source,
            sourceLayer: feature.sourceLayer || ''
          };

          localMap.setFeatureState(
            {id: feature.id || '', source: feature.source, sourceLayer: feature.sourceLayer},
            {hover: true}
          );
        }
      });

      localMap.on('mouseleave', clickableLayers, () => {
        localMap.getCanvas().style.cursor = '';

        if (hoveredFeatureRef.current) {
          localMap.setFeatureState(hoveredFeatureRef.current, {hover: false});
        }
      });

      setMap(localMap);
    });

    const navControl = new mapboxgl.NavigationControl({showCompass: true, showZoom: false, visualizePitch: true});

    const onMapMoveEnd = () => {
      const isAligned = localMap.getPitch() === 0 && localMap.getBearing() === 0;
      const hasControl = localMap.hasControl(navControl);

      if (!isAligned && !hasControl) {
        localMap.addControl(navControl, 'top-right');
      } else if (isAligned && hasControl) {
        localMap.removeControl(navControl);
      }
    };

    localMap.on('moveend', onMapMoveEnd);
  }, [isLoadingTheme, map, baseStyle]);

  useEffect(() => {
    if (!map) return;

    return () => {
      map?.remove();
      setMap(null);
      mapCreated.current = false;
    };
  }, [map]);

  const variantChanged = previousVariant.current !== variant;

  const shouldUpdate =
    variantChanged ||
    previousHighlightedCountryCodes.current !== highlightedCountryCodes ||
    previousDarkMode.current !== isDarkMode ||
    previousPlaces.current !== places;

  if (shouldUpdate && map && mapContainer.current && baseStyle) {
    if (variant === 'countries') {
      const containerSize = mapContainer.current.getBoundingClientRect();
      const size = Math.min(containerSize.width, containerSize.height);

      map.flyTo({
        zoom: getGlobeZoom(size),
        pitch: 0,
        bearing: 0,
        center: getCenter(places),
        duration: 1000
      });
    }

    map.setStyle(getVariantStyle({baseStyle, variant, isDark, highlightedCountryCodes, places}));
  }

  if (variantChanged && map) map.setMaxZoom(getVariantMaxZoom(variant));

  previousDarkMode.current = isDarkMode;
  previousVariant.current = variant;
  previousHighlightedCountryCodes.current = highlightedCountryCodes;

  return {mapContainer, map};
};

export default useMapStyle;
